import { PureComponent, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Container, Grid, Segment, Button, Icon } from 'semantic-ui-react';
import { AvForm, AvField } from 'availity-reactstrap-validation';

import { filter, map, isEmpty, find, get } from '../../utils/lodash';
import { CustomSegment } from '../../components';
import loginLogo from '../../assets/whitebox-login-logo.svg';
import { HtmlService } from '../../services';

import {
  getPublicPolicies,
  getPublicOutcomes,
  getPublicMethods,
  generatePublicQuestion,
  getPublicEvalAssesmentTypes,
  getPublicEvalReasons,
} from '../../store/actions';

import './questionGenerator.scss';

class QuestionGenerator extends PureComponent {
  constructor(props) {
    super(props);
    this.printContentRef = createRef();
    this.state = {
      loading: false,
      objectives: [],
      outcome: {},
      selectedPolicy: null,
      selectedMethods: null,
      selectedOutcomes: null,
      question: {},
    };
  }

  componentDidMount() {
    const {
      getPublicPolicies,
      getPublicOutcomes,
      getPublicMethods,
      getPublicEvalAssesmentTypes,
      getPublicEvalReasons,
    } = this.props;

    getPublicPolicies();
    getPublicOutcomes();
    getPublicMethods();
    getPublicEvalAssesmentTypes();
    getPublicEvalReasons();
  }

  async onChangeHandler(type, value) {
    switch (type) {
      case 'policy':
        await this.setState({ selectedPolicy: null });
        this.setPolicy(value);
        break;
      case 'outcome':
        const [outcome] = filter(this.state.objectives, {
          id: Number(value),
        });
        await this.setState({
          selectedOutcomes: Number(value),
          outcome,
        });
        this.generateQuestion();
        break;
      case 'method':
        await this.setState({ selectedMethods: Number(value) });
        this.generateQuestion();
        break;

      default:
        break;
    }
  }

  setPolicy(value) {
    const { outcomes } = this.props;

    const objectives = filter(outcomes, { policyId: Number(value) });
    this.setState({
      objectives: objectives,
      selectedPolicy: value,
      selectedMethods: null,
      selectedOutcomes: null,
      question: {},
    });
  }

  async generateQuestion() {
    const { selectedMethods, selectedOutcomes } = this.state;
    const { generatePublicQuestion } = this.props;

    if (selectedMethods && selectedOutcomes) {
      await this.setState({ loading: true });
      await generatePublicQuestion({
        methodId: selectedMethods,
        outcomeId: selectedOutcomes,
      });
      this.setState({ question: this.props.question, loading: false });
    }
  }

  renderEvaluationReasonsList() {
    const { evalReasons } = this.props;
    return map(evalReasons, (reason, index) => {
      return (
        <li key={`${reason.id || reason.name}-${index}`}>
          <span className="option-marker radio" />
          <span>{reason.name}</span>
        </li>
      );
    });
  }

  renderEvaluationAssessmentTypes() {
    const { evalAssesmentTypes } = this.props;
    return map(evalAssesmentTypes, (type, index) => {
      return (
        <li key={`${type.id || type.name}-${index}`}>
          <span className="option-marker checkbox" />
          <span>{type.name}</span>
        </li>
      );
    });
  }

  renderScaleBar() {
    return (
      <div className="rating-grid">
        <div className="rating-segmented">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
            <div className="rating-segment" key={`rating-point-${value}`}>
              {value}
            </div>
          ))}
        </div>
      </div>
    );
  }

  getMethodDisplayName(method = {}) {
    const rawName =
      get(method, 'details', null) || get(method, 'method', null) || '';
    const normalized = String(rawName)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    if (normalized.includes('cc intercept participants')) {
      return 'Quick Response (Intercept)';
    }

    return rawName;
  }

  isQuickResponseCCOption(method = {}) {
    const rawName =
      get(method, 'details', null) || get(method, 'method', null) || '';
    const normalized = String(rawName)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    return normalized.includes('cc intercept participants');
  }

  isQuickResponseNonCCOption(method = {}) {
    const rawName =
      get(method, 'details', null) || get(method, 'method', null) || '';
    const normalized = String(rawName)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    return (
      normalized.includes('quick response') &&
      !normalized.includes('cc') &&
      !normalized.includes('culture counts')
    );
  }

  formatQuestionHtml(value) {
    if (!value) {
      return '';
    }

    const formatted = value.replace(
      /\+\+\+([\s\S]*?)\*\*\*/g,
      '<strong>$1</strong>'
    );

    // Remove leading/trailing empty paragraphs/divs/line-break blocks often returned by templates.
    return formatted
      .replace(
        /^(<(p|div)[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/(p|div)>\s*|<br\s*\/?>\s*|\s)+/gi,
        ''
      )
      .replace(
        /(<(p|div)[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/(p|div)>\s*|<br\s*\/?>\s*|\s)+$/gi,
        ''
      )
      .replace(/(<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/gi, '')
      .replace(/(<br\s*\/?>\s*)+$/gi, '')
      .trim();
  }

  hasRenderableHtml(value) {
    const formatted = this.formatQuestionHtml(value);
    const plain = formatted
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();
    return plain.length > 0;
  }

  isQuickResponseCCMethod() {
    const { selectedMethods } = this.state;
    const { methods } = this.props;
    const selectedMethod = find(
      methods,
      (method) => method.id === selectedMethods
    );
    return this.isQuickResponseCCOption(selectedMethod);
  }

  getPrintStyles() {
    return `
      <style>
        @page { size: A4; margin: 10mm; }
        body { margin: 0; padding: 0; }
      </style>
    `;
  }

  getDocumentStylesheets() {
    return Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((node) => node.outerHTML)
      .join('');
  }

  getPrintHeader() {
    const { selectedPolicy, selectedMethods, outcome } = this.state;
    const { policies, methods } = this.props;
    const selectedMethod = find(
      methods,
      (method) => method.id === selectedMethods
    );
    const selectedDomain = find(
      policies,
      (policy) => String(policy.id) === String(selectedPolicy)
    );
    const objectiveTitle = get(
      outcome,
      'title',
      'Generated Evaluation Question'
    );
    const methodLabel =
      this.getMethodDisplayName(selectedMethod) || 'Not selected';
    const domainLabel = get(selectedDomain, 'policyName', 'Not selected');

    return `
      <p class="method-text">Method: ${methodLabel}</p>
      <h1>${objectiveTitle}</h1>
      <p class="domain-text">Domain: ${domainLabel}</p>
    `;
  }

  isInterviewMethod() {
    const { selectedMethods } = this.state;
    const { methods } = this.props;
    const selectedMethod = find(
      methods,
      (method) => method.id === selectedMethods
    );
    const methodText = `${get(selectedMethod, 'method', '')} ${get(
      selectedMethod,
      'details',
      ''
    )}`.toLowerCase();
    return methodText.includes('interview');
  }

  renderPrintMetadata() {
    const showInterviewer = this.isInterviewMethod();
    return `
      <div class="print-details">
        <div class="detail-row"><div class="detail-label">Activity Name</div><div class="detail-line"></div></div>
        <div class="detail-row"><div class="detail-label">Organisation</div><div class="detail-line"></div></div>
        <div class="detail-row"><div class="detail-label">Date</div><div class="detail-line"></div></div>
        ${
          showInterviewer
            ? '<div class="detail-row"><div class="detail-label">Interviewer</div><div class="detail-line"></div></div>'
            : ''
        }
      </div>
    `;
  }

  renderPrintOptionList(items, markerType = 'checkbox') {
    return `
      <ul class="option-list">
        ${map(
          items,
          (item) =>
            `<li><span class="option-marker ${markerType}"></span><span>${item.name}</span></li>`
        ).join('')}
      </ul>
    `;
  }

  renderPrintScale() {
    return `
      <div class="rating-grid">
        <div class="rating-segmented">
          ${Array.from({ length: 10 }, (_, i) => i + 1)
            .map(
              (value) => `
                <div class="rating-segment">${value}</div>
              `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  renderPrintAnswerLines(lines = 4) {
    return `
      <div class="answer-lines">
        ${Array.from({ length: lines })
          .map(() => '<div class="answer-line"></div>')
          .join('')}
      </div>
    `;
  }

  getPrintableSurveyHtml() {
    const { question, selectedMethods } = this.state;
    const { evalReasons, evalAssesmentTypes } = this.props;
    const formattedMethodPreamble = this.formatQuestionHtml(
      get(question, 'method.preamble', '')
    );
    const formattedPreamble = this.formatQuestionHtml(
      get(question, 'preamble', '')
    );
    const formattedQuestion1 = this.formatQuestionHtml(
      get(question, 'question1', '')
    );
    const formattedQuestion2 = this.formatQuestionHtml(
      get(question, 'question2', '')
    );
    const hasIntro = this.hasRenderableHtml(
      get(question, 'method.preamble', '')
    );
    const isQuickResponseCC = this.isQuickResponseCCMethod();

    const selfAssessedBlock =
      selectedMethods === 9
        ? `
      <div class="qs-section">
        <div class="qs-title">Self-assessed Method Inputs</div>
        <div class="qs-body">
          <p><strong>Reason for this evaluation method</strong> (choose one)</p>
          ${this.renderPrintOptionList(evalReasons || [], 'radio')}
          <p><strong>How did you form the assessment for this evaluation?</strong> (choose one or more)</p>
          ${this.renderPrintOptionList(evalAssesmentTypes || [], 'checkbox')}
        </div>
      </div>`
        : '';

    const question2Block = formattedQuestion2
      ? `
        <div class="rich-text">${formattedQuestion2}</div>
        ${this.renderPrintScale()}
      `
      : '';

    const quantitativeBlock = isQuickResponseCC
      ? ''
      : `
        <div class="qs-section">
          <div class="qs-title">Question(s) for quantitative data</div>
          <div class="qs-body">
            <div class="rich-text">${formattedQuestion1}</div>
            ${this.renderPrintScale()}
            ${question2Block}
          </div>
        </div>
      `;

    const introductionBlock = hasIntro
      ? `
        <div class="qs-section">
          <div class="qs-title">Introduction</div>
          <div class="qs-body rich-text">${formattedMethodPreamble}</div>
        </div>
      `
      : '';

    return `
      <div class="print-sheet">
        ${this.getPrintHeader()}
        ${this.renderPrintMetadata()}
        ${introductionBlock}
        <div class="qs-section">
          <div class="qs-title">Outcome description</div>
          <div class="qs-body rich-text">
            ${formattedPreamble}
            ${isQuickResponseCC ? this.renderPrintScale() : ''}
          </div>
        </div>
        ${selfAssessedBlock}
        ${quantitativeBlock}
        <div class="qs-section">
          <div class="qs-title">Question for qualitative data</div>
          <div class="qs-body">
            <p>Can you tell us <strong>why</strong> you gave this rating?</p>
            ${this.renderPrintAnswerLines(4)}
          </div>
        </div>
      </div>
    `;
  }

  downloadAsPdf() {
    const { question, outcome } = this.state;
    if (isEmpty(question)) {
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }

    const documentTitle = get(outcome, 'title', 'Evaluation Question');

    printWindow.document.write(`
      <html>
        <head>
          <title>${documentTitle}</title>
          ${this.getDocumentStylesheets()}
          ${this.getPrintStyles()}
        </head>
        <body>
          ${this.getPrintableSurveyHtml()}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.document.title = documentTitle;
    try {
      printWindow.history.replaceState({}, '', '/generate-question-print');
    } catch (error) {
      // ignore when browser blocks history updates in print window
    }
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }

  render() {
    const {
      objectives,
      selectedMethods,
      selectedOutcomes,
      selectedPolicy,
      question,
      loading,
      outcome,
    } = this.state;
    const { policies, methods, t } = this.props;
    const getMethod = find(methods, (i) => i.id === selectedMethods);
    const availableMethods = filter(
      methods,
      (method) => !this.isQuickResponseNonCCOption(method)
    );
    const hasIntro = this.hasRenderableHtml(
      get(question, 'method.preamble', '')
    );
    const isQuickResponseCC = this.isQuickResponseCCMethod();

    return (
      <div>
        <Container fluid className="container-header">
          <Grid.Row>
            {/* <h2 className="whiteboxHeading">{APP_NAME}</h2> */}
            <Link className="whiteboxHeading" to={'/'}>
              <img alt="TaksoLogo" className="takso-logo" src={loginLogo} />
            </Link>
          </Grid.Row>

          <Grid.Row className="mainRow">
            <CustomSegment
              title={t('question_generator.title')}
              children={
                <Segment
                  className="description-segment"
                  textAlign="left"
                  loading={loading}
                >
                  {HtmlService.convertToInnerHtml({
                    children: t('question_generator.full_description_html'),
                    richText: true,
                    htmlConversionStyle: 'para',
                    returnAs: 'html',
                  })}

                  <hr className="hr-padding" />

                  <AvForm
                    ref={(c) => (this.form = c)}
                    className="activity-form"
                  >
                    <Grid>
                      <Grid.Row className="row-center">
                        <Grid.Column
                          mobile={16}
                          tablet={5}
                          computer={5}
                          className="column-padding"
                        >
                          <div className="column-bar">
                            <AvField
                              className="generator-inputs"
                              type="select"
                              name="policy"
                              label={t('common.policy_domain').toUpperCase()}
                              onChange={(e) =>
                                this.onChangeHandler('policy', e.target.value)
                              }
                            >
                              <option value="" disabled />
                              {map(policies, (item, i) => (
                                <option key={i} value={item.id}>
                                  {item.policyName}
                                </option>
                              ))}
                            </AvField>
                            <label className="field-label">
                              {t('question_generator.select_policy_domain')}
                            </label>
                          </div>
                        </Grid.Column>
                        <Grid.Column
                          mobile={16}
                          tablet={5}
                          computer={5}
                          className="column-padding"
                        >
                          {selectedPolicy ? (
                            <div className="column-bar">
                              <AvField
                                className="generator-inputs"
                                type="select"
                                name="outcome"
                                label={t('common.objective').toUpperCase()}
                                onChange={(e) =>
                                  this.onChangeHandler(
                                    'outcome',
                                    e.target.value
                                  )
                                }
                              >
                                <option value="" disabled />
                                {map(objectives, (item, i) => (
                                  <option key={i} value={item.id}>
                                    {item.title}
                                  </option>
                                ))}
                              </AvField>
                              <label className="field-label">
                                {t('question_generator.select_objective')}
                              </label>
                            </div>
                          ) : null}
                        </Grid.Column>
                        <Grid.Column
                          mobile={16}
                          tablet={5}
                          computer={5}
                          className="column-padding"
                        >
                          {selectedOutcomes ? (
                            <div className="column-bar">
                              <AvField
                                className="generator-inputs"
                                type="select"
                                name="method"
                                label={t('common.method').toUpperCase()}
                                onChange={(e) =>
                                  this.onChangeHandler('method', e.target.value)
                                }
                              >
                                <option value="" disabled />
                                {map(availableMethods, (item, i) => (
                                  <option key={i} value={item.id}>
                                    {this.getMethodDisplayName(item)}
                                  </option>
                                ))}
                              </AvField>
                              <label className="field-label">
                                {t('question_generator.select_method')}
                              </label>
                            </div>
                          ) : null}
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </AvForm>

                  {!isEmpty(question) ? (
                    <div>
                      <hr className="hr-padding" />

                      <div className="question-actions">
                        <Button
                          className="download-pdf-btn"
                          color="orange"
                          icon
                          labelPosition="left"
                          onClick={() => this.downloadAsPdf()}
                        >
                          <Icon name="download" />
                          {t('question_generator.download_pdf')}
                        </Button>
                      </div>

                      <div ref={this.printContentRef}>
                        {hasIntro ? (
                          <div className="qs-section">
                            <div className="qs-title">
                              {t('question_generator.introduction')}
                            </div>
                            <div
                              className="qs-body rich-text"
                              dangerouslySetInnerHTML={{
                                __html: this.formatQuestionHtml(
                                  question.method.preamble
                                ),
                              }}
                            />
                          </div>
                        ) : null}

                        <div className="qs-section">
                          <div className="qs-title">
                            {t('question_generator.outcome_description')}
                          </div>
                          <div className="qs-body rich-text">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: this.formatQuestionHtml(
                                  question.preamble
                                ),
                              }}
                            />
                            {isQuickResponseCC ? this.renderScaleBar() : null}
                          </div>
                        </div>

                        {isQuickResponseCC ? null : (
                          <div className="qs-section">
                            <div className="qs-title">
                              {t(
                                'question_generator.question_for_quantitative_data'
                              )}
                            </div>
                            <div className="qs-body rich-text">
                              {selectedMethods === 9 ? (
                                <div className="evaluation-method-section">
                                  <h4>
                                    {t(
                                      'question_generator.reason_for_this_evaluation_method'
                                    )}
                                  </h4>
                                  <ul className="option-list">
                                    {this.renderEvaluationReasonsList()}
                                  </ul>

                                  {HtmlService.convertToInnerHtml({
                                    children: t(
                                      'question_generator.quantitative_data_html'
                                    ),
                                    richText: true,
                                    htmlConversionStyle: 'para',
                                    returnAs: 'html',
                                  })}

                                  <ul className="option-list">
                                    {this.renderEvaluationAssessmentTypes()}
                                  </ul>
                                </div>
                              ) : null}

                              <div
                                dangerouslySetInnerHTML={{
                                  __html: this.formatQuestionHtml(
                                    question.question1
                                  ),
                                }}
                              />
                              {this.renderScaleBar()}
                              {question.question2 ? (
                                <div className="question-follow-up">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: this.formatQuestionHtml(
                                        question.question2
                                      ),
                                    }}
                                  />
                                  {this.renderScaleBar()}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}

                        {get(getMethod, 'method', null) ===
                        'Intercept' ? null : (
                          <div className="qs-section">
                            <div className="qs-title">
                              {t(
                                'question_generator.question_for_qualitative_data'
                              )}
                            </div>
                            <div className="qs-body rich-text">
                              <p>
                                {t(
                                  'question_generator.can_you_tell_us_more_about_why_you_gave_this_rating'
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </Segment>
              }
            />
          </Grid.Row>
        </Container>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    policies: state.extra.policies,
    methods: state.extra.methods,
    outcomes: state.extra.outcomes,
    question: state.extra.generatedQuestion,
    evalReasons: state.extra.evalReasons,
    evalAssesmentTypes: state.extra.evalAssesmentTypes,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getPublicPolicies,
      getPublicOutcomes,
      getPublicMethods,
      generatePublicQuestion,
      getPublicEvalAssesmentTypes,
      getPublicEvalReasons,
    },
    dispatch
  );
};

QuestionGenerator = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(QuestionGenerator));

export default QuestionGenerator;
